"use client";

import { c as _c } from "react/compiler-runtime";
import type { Entry } from "contentful";
import { useEffect, useRef, useState } from "react";
import { BLOG_PAGE_SIZE } from "@/constants/BlogPageSize";
import type { TypeBlogArticleSkeleton } from "@/types/contentful/generated";
import { fetchBlogPage } from "./actions";
function getPostPages(posts: Entry<TypeBlogArticleSkeleton, undefined, string>[], initialPage: number) {
  const pages: {
    page: number;
    posts: Entry<TypeBlogArticleSkeleton, undefined, string>[];
  }[] = [];
  for (let i = 0; i < posts.length; i += BLOG_PAGE_SIZE) {
    const pageNum = initialPage + Math.floor(i / BLOG_PAGE_SIZE);
    pages.push({
      page: pageNum,
      posts: posts.slice(i, i + BLOG_PAGE_SIZE)
    });
  }
  return pages;
}
interface UseBlogPaginationArgs {
  initialPosts: Entry<TypeBlogArticleSkeleton, undefined, string>[];
  initialPage: number;
  resumeFromPage: number;
  totalPages: number;
  categoryId?: string;
  /** Comma-separated IDs of this category + all descendants */
  categoryIds?: string;
}
export function useBlogPagination({
  initialPosts,
  initialPage,
  resumeFromPage,
  totalPages,
  categoryId,
  categoryIds
}: UseBlogPaginationArgs) {
  const [posts, setPosts] = useState(initialPosts);
  const [nextPage, setNextPage] = useState(resumeFromPage + 1);
  const [pageCount, setPageCount] = useState(totalPages);
  const [isLoading, setIsLoading] = useState(false);
  const hasMore = nextPage <= pageCount;
  const loadMore = async () => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    try {
      const data = await fetchBlogPage(nextPage, categoryId, categoryIds);
      if (!data) {
        console.error(`Failed to fetch blog posts for page ${nextPage}. Response:`, data);
        throw new Error(`Failed to fetch blog posts for page ${nextPage}`);
      }
      setPosts((prev: Entry<TypeBlogArticleSkeleton, undefined, string>[]) => {
        const seen = new Set(prev.map(p => p.sys.id));
        return [...prev, ...data.items.filter((p_0: Entry<TypeBlogArticleSkeleton, undefined, string>) => !seen.has(p_0.sys.id))];
      });
      setPageCount(data.totalPages);
      setNextPage(curr => curr + 1);
    } catch (err) {
      console.error("Error fetching blog posts:", err);
    } finally {
      setIsLoading(false);
    }
  };
  const postPages = getPostPages(posts, initialPage);
  return {
    posts,
    postPages,
    isLoading,
    hasMore,
    pageCount,
    loadMore
  };
}
function updateUrlWithPage(activePage: number) {
  const url = new URL(window.location.href);
  const currentUrlPage = parseInt(url.searchParams.get("page") ?? "1", 10);
  if (currentUrlPage === activePage) return;
  if (activePage === 1) {
    url.searchParams.delete("page");
  } else {
    url.searchParams.set("page", String(activePage));
  }
  window.history.replaceState(null, "", url.toString());
}
export function useUrlPageSync(resumeFromPage) {
  const $ = _c(12);
  let t0;
  if ($[0] !== resumeFromPage) {
    t0 = new Set([resumeFromPage]);
    $[0] = resumeFromPage;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const visiblePages = useRef(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sentinels = useRef(t1);
  const didScroll = useRef(resumeFromPage <= 1);
  const observerRef = useRef(null);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (observerRef.current) {
        return observerRef.current;
      }
      observerRef.current = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const page = Number(entry.target.getAttribute("data-page"));
          if (entry.isIntersecting) {
            visiblePages.current.add(page);
          } else {
            visiblePages.current.delete(page);
          }
        }
        const visible = Array.from(visiblePages.current);
        if (visible.length > 0) {
          updateUrlWithPage(Math.min(...visible));
        }
      }, {
        rootMargin: "0px 0px -60% 0px"
      });
      return observerRef.current;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getOrCreateObserver = t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (page_0, el) => {
      const prev = sentinels.current.get(page_0);
      sentinels.current.set(page_0, el);
      if (!didScroll.current) {
        return;
      }
      const observer = getOrCreateObserver();
      if (prev && prev !== el) {
        observer.unobserve(prev);
      }
      observer.observe(el);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const observeSentinel = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    const unobserveSentinel = page_1 => {
      const prev_0 = sentinels.current.get(page_1);
      sentinels.current.delete(page_1);
      visiblePages.current.delete(page_1);
      if (prev_0) {
        observerRef.current?.unobserve(prev_0);
      }
    };
    t6 = page_2 => el_0 => {
      if (el_0) {
        observeSentinel(page_2, el_0);
      } else {
        unobserveSentinel(page_2);
      }
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const setSentinelRef = t6;
  let t7;
  let t8;
  if ($[8] !== resumeFromPage) {
    t7 = () => {
      if (didScroll.current) {
        return;
      }
      const target = sentinels.current.get(resumeFromPage);
      if (target) {
        requestAnimationFrame(() => {
          target.scrollIntoView({
            behavior: "auto"
          });
          didScroll.current = true;
          const observer_0 = getOrCreateObserver();
          for (const el_1 of sentinels.current.values()) {
            observer_0.observe(el_1);
          }
        });
      }
    };
    t8 = [resumeFromPage];
    $[8] = resumeFromPage;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      setSentinelRef
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}
export function useScrollToTop(t0) {
  const $ = _c(5);
  const threshold = t0 === undefined ? 800 : t0;
  const [visible, setVisible] = useState(false);
  let t1;
  let t2;
  if ($[0] !== threshold) {
    t1 = () => {
      const onScroll = () => setVisible(window.scrollY > threshold);
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t2 = [threshold];
    $[0] = threshold;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const scrollToTop = _temp;
  let t3;
  if ($[3] !== visible) {
    t3 = {
      showScrollTop: visible,
      scrollToTop
    };
    $[3] = visible;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp() {
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}